import type { SelectOption } from 'naive-ui'
import type { ComputedRef, MaybeRefOrGetter, Ref } from 'vue'

// keepPreviousData отсутствует в пресете auto-import для @tanstack/vue-query
import { keepPreviousData } from '@tanstack/vue-query'

const DEFAULT_PAGE_SIZE = 30
const DEFAULT_SEARCH_DEBOUNCE_MS = 300
const SCROLL_LOAD_THRESHOLD = 10

export interface SelectInfinitePage<TItem> {
  items: TItem[]
  totalPages: number
}

export interface UseSelectInfiniteOptions<TItem> {
  /** Собирает query key через фабрику queryKeys с реактивным поиском */
  buildQueryKey: (search: Ref<string | undefined>) => unknown[]

  /** Загрузка страницы списка */
  fetchPage: (params: {
    page: { number: number; size: number }
    search?: string
    signal?: AbortSignal
  }) => Promise<SelectInfinitePage<TItem>>

  /** Преобразование элемента в опцию Naive UI Select */
  toOption: (item: TItem) => SelectOption

  pageSize?: number

  /** Задержка debounce для поиска, мс */
  searchDebounceMs?: number

  /** Выбранные id для удержания label и deep-link резолва */
  selectedIds?: MaybeRefOrGetter<string[]>

  /** Query key для запроса по ids (deep-link) */
  buildByIdsQueryKey?: (ids: ComputedRef<string[]>) => unknown[]

  /** Загрузка элементов по ids, которых нет в загруженных страницах */
  fetchByIds?: (ids: string[], signal?: AbortSignal) => Promise<TItem[]>
}

/** Хук для бесконечно прокручиваемого remote-select с TanStack Query */
export const useSelectInfinite = <TItem>(options: UseSelectInfiniteOptions<TItem>) => {
  const {
    buildQueryKey,
    fetchPage,
    toOption,
    pageSize = DEFAULT_PAGE_SIZE,
    searchDebounceMs = DEFAULT_SEARCH_DEBOUNCE_MS,
    selectedIds,
    buildByIdsQueryKey,
    fetchByIds,
  } = options

  const search = ref<string | undefined>()
  const searchDebounced = refDebounced(search, searchDebounceMs)
  const selectedLabelsCache = ref(new Map<string, string>())

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    error: listError,
    isError: isListError,
  } = useInfiniteQuery({
    queryKey: buildQueryKey(searchDebounced),
    initialPageParam: 1,
    queryFn: ({ pageParam, signal }) =>
      fetchPage({
        page: {
          number: pageParam,
          size: pageSize,
        },
        search: searchDebounced.value?.trim() || undefined,
        signal,
      }),
    getNextPageParam: (lastPage, allPages) => {
      const nextPage = allPages.length + 1
      return nextPage <= lastPage.totalPages ? nextPage : undefined
    },
    placeholderData: keepPreviousData,
  })

  const loadedItems = computed(() => data.value?.pages.flatMap((page) => page.items) ?? [])

  const cacheOptionLabel = (item: TItem | undefined) => {
    if (!item) {
      return
    }

    const option = toOption(item)
    if (option.value === undefined || option.value === null) {
      return
    }

    selectedLabelsCache.value.set(String(option.value), String(option.label ?? option.value))
  }

  watch(loadedItems, (items) => items.forEach(cacheOptionLabel), { immediate: true })

  const unresolvedSelectedIds = computed(() => {
    if (!selectedIds) {
      return []
    }

    return toValue(selectedIds).filter((id) => !selectedLabelsCache.value.has(id))
  })

  const canResolveByIds = Boolean(fetchByIds && buildByIdsQueryKey)

  const {
    data: resolvedByIdsItems,
    error: byIdsError,
    isError: isByIdsError,
  } = useQuery({
    queryKey: canResolveByIds ? buildByIdsQueryKey!(unresolvedSelectedIds) : ['selectInfiniteByIds'],
    enabled: computed(() => canResolveByIds && unresolvedSelectedIds.value.length > 0),
    queryFn: ({ signal }) => fetchByIds!(unresolvedSelectedIds.value, signal),
  })

  watch(resolvedByIdsItems, (items) => items?.forEach(cacheOptionLabel), { immediate: true })

  watch(
    () => (selectedIds ? toValue(selectedIds) : []),
    (ids) => {
      for (const id of ids) {
        const item = loadedItems.value.find((loadedItem) => {
          const option = toOption(loadedItem)
          return String(option.value) === id
        })

        cacheOptionLabel(item)
      }
    },
    { immediate: true },
  )

  const resultOptions = computed(() => {
    const fromApi = loadedItems.value.map((item) => toOption(item))
    // value опции может быть числом — нормализуем к строке, чтобы дедупликация не зависела от типа id
    const apiIds = new Set(fromApi.map((option) => String(option.value)))
    const selected = selectedIds ? toValue(selectedIds) : []

    const cachedSelected = selected
      .filter((id) => !apiIds.has(id) && selectedLabelsCache.value.has(id))
      .map((id) => ({
        label: selectedLabelsCache.value.get(id) ?? id,
        value: id,
      }))

    return [...cachedSelected, ...fromApi]
  })

  const error = computed(() => listError.value ?? byIdsError.value)
  const isError = computed(() => isListError.value || isByIdsError.value)

  const handleSearch = (query: string) => {
    search.value = query
  }

  const handleScroll = (event: Event) => {
    const target = event.currentTarget

    if (!(target instanceof HTMLElement)) {
      return
    }

    const { scrollTop, offsetHeight, scrollHeight } = target
    const isNearBottom = scrollTop + offsetHeight >= scrollHeight - SCROLL_LOAD_THRESHOLD

    if (!isNearBottom || !hasNextPage.value || isFetchingNextPage.value) {
      return
    }

    fetchNextPage()
  }

  return reactive({
    options: resultOptions,
    loading: isFetching,
    filterable: true,
    remote: true,
    resetMenuOnOptionsChange: false,
    onScroll: handleScroll,
    onSearch: handleSearch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    error,
    isError,
  })
}
